import { Line } from '@models/line.model';
import { User } from '@app/shared/models';
import { Equipment } from '@models/equipment.model';
import { ToolStorage } from '@app/tool/model/toolLocation.model';
import { Tool } from '@app/tool/model/tool.model';
import { AppUtils } from '@app/shared/util/app.utils';
import * as _ from 'lodash';
import * as moment from 'moment';
import { PartNumber } from '@models/partNumber.model';
import { InsertionCycle } from '@app/shared/models/insertion-cycle.model';
import { CodingTypeValue, normalizeCodingTypeValue } from '@app/shared/models/enums/codingType.enum';

export class ToolFormDetails {
    isSet = false;
    setSize = 0;
    nrOfSpareTools = 0;
    name = '';
    description = '';
    mesCodes: Array<string | null> = [];
    supplierCodes: string[] = [];
    inventoryNumbers: string[] = [];
    toolControlNames: string[] = [];
    codingTypes: CodingTypeValue[] = [];
    isToolLifeMonitoredInSap = false;
    isToolLifeMonitoredInMes = false;
    isToolLifeMonitoredInPlc = false;
    hasMaintenaceAtLine = false;
    excludedFromMaintenance = false;
    directContactWithPCB = false;
    manualHandling = false;
    owner: User;
    line: Line;
}

export class ToolFormLifecycle {
    currentProductionCycles = '0';
    currentProductionTime = 0;
    toolRevisionPercent = '75';
    expirationDate: '';
    partNumbersCount = 1;
}

export class ToolFormModel {
    id: number;
    duplicateId: number;
    isSet = false;
    setSize = 0;
    isSpare = false;
    nrOfSpareTools = 0;
    name = '';
    description = '';
    mesCodes: Array<string | null> = [];
    supplierCodes: string[] = [];
    inventoryNumbers: string[] = [];
    toolControlNames: string[] = [];
    codingTypes: CodingTypeValue[] = [];
    ownerId = 0;
    lineId = 0;
    maintenancePlanId = null;
    isToolLifeMonitoredInSap = false;
    isToolLifeMonitoredInMes = false;
    isToolLifeMonitoredInPlc = false;
    hasMaintenaceAtLine = false;
    isToolStationedAtERSA = false;
    excludedFromReturn = false;
    excludedFromMaintenance = false;
    directContactWithPCB = false;
    manualHandling = false;

    currentProductionCycles = '0';
    currentProductionTime = 0;
    expirationDate: string;
    toolRevisionPercent = '75';
    partNumbersCount = 1;

    partNumberIds: Array<number> = [];
    insertionCycleIds: Array<number> = [];
    equipmentIds: Array<number> = [];

    owner = {};
    line = {};
    partNumbers: PartNumber[] = [];
    insertionCycles: InsertionCycle[] = [];
    equipments: Equipment[] = [];
    mainEquipment: Equipment[] = [];
    secondaryEquipment: Equipment[] = [];

    toolStorage: ToolStorage[] = [];

    public static toolCopyOr(tool: Tool) {
        const toolFormModel = new ToolFormModel();
        // Details form
        toolFormModel.id = tool.id;
        toolFormModel.isSet = false;
        toolFormModel.setSize = 0;
        toolFormModel.isSpare = tool.details.isSpare;
        toolFormModel.nrOfSpareTools = 0;
        toolFormModel.name = tool.name + AppUtils.COPY_STRING;
        toolFormModel.description = tool.details.description;
        toolFormModel.mesCodes = [tool.mesCode];
        toolFormModel.supplierCodes = [tool.supplierCode];
        toolFormModel.inventoryNumbers = [tool.inventoryNumber];
        toolFormModel.toolControlNames = [tool.toolControlName];
        toolFormModel.codingTypes = [normalizeCodingTypeValue(tool.codingType)];
        toolFormModel.ownerId = tool.owner.id;
        toolFormModel.owner = new User(tool.owner);
        toolFormModel.line = tool.mainLine;
        toolFormModel.lineId = tool.mainLine.id;
        toolFormModel.isToolLifeMonitoredInSap = tool.details.isToolLifeMonitoredInSap;
        toolFormModel.isToolLifeMonitoredInMes = tool.details.isToolLifeMonitoredInMes;
        toolFormModel.isToolLifeMonitoredInPlc = tool.details.isToolLifeMonitoredInPlc;
        toolFormModel.hasMaintenaceAtLine = tool.details.hasMaintenaceAtLine;
        toolFormModel.excludedFromMaintenance = tool.details.excludedFromMaintenance;
        toolFormModel.isToolStationedAtERSA = tool.details.isToolStationedAtERSA;
        toolFormModel.excludedFromReturn = tool.details.excludedFromReturn;
        toolFormModel.maintenancePlanId = tool.details.maintenancePlanId;
        toolFormModel.directContactWithPCB = tool.details.directContactWithPCB;
        toolFormModel.manualHandling = tool.details.manualHandling;

        // Location form
        toolFormModel.toolStorage = [
            new ToolStorage({
                key: tool.name,
                liftNumber: tool.liftNumber,
                liftShelf: tool.liftShelf,
                shelfPosition: tool.shelfPosition
            })
        ];

        // Lifecycle form
        toolFormModel.currentProductionCycles =
            tool.details.currentProductionCycles != null ? String(tool.details.currentProductionCycles) : '0';
        toolFormModel.currentProductionTime = tool.details.currentProductionTime;
        toolFormModel.expirationDate = moment(tool.details.expirationDate, 'M/D/YYYY H:mm').valueOf().toString();
        toolFormModel.toolRevisionPercent =
            tool.details.toolRevisionPercent != null ? String(tool.details.toolRevisionPercent) : '75';
        toolFormModel.partNumbersCount = tool.details.partNumbersCount;

        toolFormModel.partNumbers = tool.details.partNumbers.map((partNumber) => {
            return new PartNumber(partNumber);
        });
        toolFormModel.insertionCycles = tool.details.insertionCycles.map((ic) => {
            return new InsertionCycle(ic);
        });

        toolFormModel.mainEquipment = _.filter(tool.details.equipment, (equipment) => {
            return equipment.line.id === tool.mainLine.id;
        }).map((equipment) => {
            return new Equipment(equipment);
        });

        toolFormModel.secondaryEquipment = _.filter(tool.details.equipment, (equipment) => {
            return equipment.line.id !== tool.mainLine.id;
        }).map((equipment) => {
            return new Equipment(equipment);
        });
        return toolFormModel;
    }

    constructor(details: any = {}) {
        this.partNumbers = details.partNumbers || [];
        this.equipments = details.equipment || [];
        this.insertionCycles = details.insertionCycles || [];
    }

    setData(data) {
        Object.assign(this, data);
    }

    toDTO() {
        this.equipmentIds = this.mainEquipment.map((eq) => eq.id).concat(this.secondaryEquipment.map((eq) => eq.id));
        this.partNumberIds = this.partNumbers.map((pn) => pn.id);
        this.insertionCycleIds = this.insertionCycles.map((ic) => ic.id);
        const toolDTO: any = Object.assign({}, this);
        delete toolDTO.equipments;
        delete toolDTO.mainEquipment;
        delete toolDTO.secondaryEquipment;
        delete toolDTO.partNumbers;
        delete toolDTO.insertionCycles;

        toolDTO.expirationDate = toolDTO.expirationDate !== 0 ? toolDTO.expirationDate : null;
        toolDTO.maintenancePlanId = toolDTO.maintenancePlanId === 0 ? null : toolDTO.maintenancePlanId;
        
        // TODO Hackerman fixes for invalid data submissions (null instead of boolean)
        toolDTO.toolStorage = this.toolStorage;
        toolDTO.isToolLifeMonitoredInMes = !!toolDTO.isToolLifeMonitoredInMes;
        toolDTO.isToolLifeMonitoredInSap = !!toolDTO.isToolLifeMonitoredInSap;
        toolDTO.isToolLifeMonitoredInPlc = !!toolDTO.isToolLifeMonitoredInPlc;
        toolDTO.hasMaintenaceAtLine = !!toolDTO.hasMaintenaceAtLine;
        toolDTO.isToolStationedAtERSA = !!toolDTO.isToolStationedAtERSA;
        toolDTO.excludedFromReturn = !!toolDTO.excludedFromReturn;
        toolDTO.excludedFromMaintenance = !!toolDTO.excludedFromMaintenance;
        toolDTO.directContactWithPCB = !!toolDTO.directContactWithPCB;
        toolDTO.manualHandling = !!toolDTO.manualHandling;
        toolDTO.isSet = !!toolDTO.isSet;
        toolDTO.isSpare = !!toolDTO.isSpare;
        return toolDTO;
    }
}
